/**
 * Page Initialization for Tracking
 *
 * PURPOSE:
 * This script runs on every page load and:
 * 1. Reads page data embedded by Twig (window.__GO4JOBS_PAGE__)
 * 2. Pushes appropriate tracking events
 * 3. Handles static caching gracefully
 *
 * WHY THIS APPROACH?
 * - Static caching means we can't rely on server-side tracking
 * - We embed data in HTML, then JavaScript reads and tracks it
 * - This ensures tracking works even on cached pages
 */

import {
    trackViewItem,
    trackAddToCart,
    trackPurchase,
    trackSpontaneousApplication,
    trackBlogView,
    type GA4JobItem,
    type UserData
} from '~/js/utils/job-data-layer';

import { pushEvent } from '~/js/utils/datalayer';

interface PageData {
    type: 'job_detail' | 'application_page' | 'spontaneous_application' | 'blog_article';
    transactionId?: string;
    job?: GA4JobItem;
    formSubmitted?: boolean;
    blogTitle?: string;
    blogCategory?: string;
    userData?: UserData;
}

declare global {
    interface Window {
        __GO4JOBS_PAGE__?: PageData;
    }
}

async function sha256(str: string): Promise<string> {
    const buffer = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

async function hashPii(email?: string | null, phone?: string | null): Promise<Record<string, string> | undefined> {
    const userData: Record<string, string> = {};
    if (email) {
        userData.sha256_email_address = await sha256(email.toLowerCase().trim());
    }
    if (phone) {
        let normalized = phone.replace(/[^\d+]/g, '');
        if (!normalized.startsWith('+')) {
            normalized = '+32' + normalized.replace(/^0/, '');
        }
        userData.sha256_phone_number = await sha256(normalized);
    }
    return Object.keys(userData).length > 0 ? userData : undefined;
}

/**
 * Stashed purchases older than this are dropped unfired - a submission that
 * hasn't confirmed by then is treated as abandoned, and this also stops a
 * stale stash from firing if the user reaches a confirmation page days
 * later through some unrelated route.
 */
const PENDING_PURCHASE_TTL_MS = 30 * 60 * 1000;

/**
 * Initialize page-level tracking
 * Called on DOMContentLoaded
 */
export function initPageTracking(): void {
    const pageData = window.__GO4JOBS_PAGE__;

    const pendingPurchase = sessionStorage.getItem('__go4jobs_pending_purchase');

    if (pendingPurchase) {
        // Consume the stash on this load no matter what - it must never
        // survive to fire on a later, unrelated page load.
        sessionStorage.removeItem('__go4jobs_pending_purchase');

        // Only a page load that Formie itself confirms as a successful
        // submission (the server-set `formSubmitted` flag) may fire the
        // purchase - the stash was written on native `submit`, before
        // validation, so its mere presence doesn't mean the submission
        // succeeded.
        if (pageData?.formSubmitted) {
            try {
                const { type, job, transactionId, email, phone, stashedAt } = JSON.parse(pendingPurchase);
                const isFresh = typeof stashedAt === 'number' && (Date.now() - stashedAt) <= PENDING_PURCHASE_TTL_MS;

                if (isFresh && type === 'application' && job) {
                    hashPii(email, phone).then(userData => {
                        trackPurchase(transactionId, job, userData);
                        if (userData) {
                            pushEvent('user_info_set', { user_data: userData });
                        }
                    });
                } else if (isFresh && type === 'spontaneous') {
                    hashPii(email, phone).then(userData => {
                        trackSpontaneousApplication(transactionId, userData);
                        if (userData) {
                            pushEvent('user_info_set', { user_data: userData });
                        }
                    });
                }
            } catch (e) {
                // Malformed data, ignore
            }
        }
    }

    if (!pageData) {
        // No page data embedded - nothing to track
        return;
    }

    // Handle different page types
    switch (pageData.type) {
        case 'job_detail':
            handleJobDetailPage(pageData);
            break;

        case 'application_page':
            handleApplicationPage(pageData);
            break;

        case 'spontaneous_application':
            handleSpontaneousApplicationPage(pageData);
            break;

        case 'blog_article':
            handleBlogPage(pageData);
            break;
    }
}

/**
 * Handle job detail page tracking
 */
function handleJobDetailPage(pageData: PageData): void {
    if (!pageData.job) return;

    // Track view_item
    trackViewItem(pageData.job);
}

/**
 * Handle application page tracking
 *
 * The purchase itself is fired from the pending-purchase stash above, never
 * from here: `pageData.transactionId` is regenerated on every render of this
 * page (including the confirmation reload), so it is never the id that was
 * actually submitted.
 */
function handleApplicationPage(pageData: PageData): void {
    if (!pageData.job) return;

    // Track add_to_cart (user initiated application process)
    trackAddToCart(pageData.job);
}

/**
 * Handle spontaneous application page tracking
 *
 * See handleApplicationPage() - the purchase fires from the pending-purchase
 * stash, not from a transaction id regenerated on this render.
 */
function handleSpontaneousApplicationPage(pageData: PageData): void {}

/**
 * Handle blog page tracking
 */
function handleBlogPage(pageData: PageData): void {
    if (pageData.blogTitle) {
        trackBlogView(pageData.blogTitle, pageData.blogCategory);
    }
}

/**
 * Initialize
 */
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPageTracking);
    } else {
        // DOM already loaded
        initPageTracking();
    }
}
